import React, { useState, useMemo, useCallback, useEffect, useRef, useContext } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import {
  Box, Typography, Button, TextField, Divider,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Paper, Stack, Chip, MenuItem, Select, FormControl,
  List, ListItem, ListItemButton, Avatar, InputBase,
  IconButton, Tooltip,
  Snackbar, Alert, CircularProgress, Badge,
} from '@mui/material';
import {
  QrCodeScanner, ReceiptLong, Add, Remove,
  Receipt, Storefront, Monitor, AccessTime, Delete,
  Image, Refresh, Menu as MenuIcon, NoteAlt,
  ExpandMore, ExpandLess, NotificationsActive,
  LockOpen, LockOutlined, SwapVert, AssignmentReturn, Search, AccountBalanceWallet,
  SwapHoriz, PersonAdd, UploadFile, LocalOffer, ManageSearch,
} from '@mui/icons-material';
import ReturnItemsDrawer from '../components/pos/ReturnItemsDrawer';
import ExchangeCheckoutModal from '../components/pos/ExchangeCheckoutModal';
import { SidebarContext } from '../context/SidebarContext';

import { setProductSearch } from '../store/productSlice';
import { clearOrder, setCurrentOrder } from '../store/orderSlice';
import CustomerSearchModal from '../components/pos/CustomerSearchModal';
import CheckoutPage from '../components/pos/CheckoutPage';
import HoldOrdersDrawer from '../components/pos/HoldOrdersDrawer';
import IncentivesDrawer from '../components/pos/incentives/IncentivesDrawer';
import useIncentiveProductSet from '../hooks/useIncentiveProductSet';
import CarryBagModal from '../components/pos/CarryBagModal';
import SalespersonDrawer from '../components/pos/SalespersonDrawer';
import DiscountDrawer from '../components/pos/DiscountDrawer';
import DiscountApprovalModal from '../components/pos/discount/DiscountApprovalModal';
import PromotionsModal from '../components/pos/PromotionsModal';
import ApprovalBanner from '../components/pos/discount/ApprovalBanner';
import { setPendingApproval, handOffPendingApproval } from '../store/discountSlice';
import { useCreateOrderMutation, useUpdateOrderMutation, useCancelOrderMutation, useHoldOrderMutation, useLazyGetOrderQuery, useLazyListOrdersQuery, useRemoveLoyaltyMutation, useDiscardHeldOrdersMutation } from '../services/orderApi';
import { useLazySearchCustomersQuery, useGetCustomerQuery } from '../services/customerApi';
import { resolveOrgSettings } from '../services/orgSettingsApi';
import { useGetReturnReasonCodesQuery } from '../services/returnApi';
import { useLazyGetMeQuery } from '../services/authApi';
import { useLazyScanProductQuery } from '../services/productApi';
import { useGetCarryBagsQuery } from '../services/carryBagApi';
import { useGetSalespersonsQuery } from '../services/salespersonApi';
import { useToast } from '../components/common/ToastProvider';
import { useLazyListSessionsQuery, useLazyGetSessionQuery } from '../services/cashSessionApi';
import { setCurrentSession } from '../store/cashSessionSlice';
import OpenSessionModal from '../components/pos/OpenSessionModal';
import CloseSessionModal from '../components/pos/CloseSessionModal';
import DiscardHeldBillsDialog from '../components/pos/DiscardHeldBillsDialog';
import CashDenominationSessionModal from '../components/pos/CashDenominationSessionModal';
import CashMovementModal from '../components/pos/CashMovementModal';
import CashOfficeModal from '../components/pos/cashOffice/CashOfficeModal';
import { useGetCashOfficeSummaryQuery } from '../services/cashOfficeApi';
import MobileScannerModal from '../components/pos/MobileScannerModal';
import StockLookupModal from '../components/pos/StockLookupModal';
import DashboardCatalogContent from './dashboard/DashboardCatalogContent';
import DashboardCustomerDetailDialog from './dashboard/DashboardCustomerDetailDialog';
import DashboardReturnSearchPanel from './dashboard/DashboardReturnSearchPanel';
import DashboardExchangeWorkflowPanel from './dashboard/DashboardExchangeWorkflowPanel';
import DashboardLeftPanel from './dashboard/DashboardLeftPanel';
import DashboardRightPanel from './dashboard/DashboardRightPanel';
import DashboardMobileControls from './dashboard/DashboardMobileControls';
import useDashboardCatalog from './dashboard/useDashboardCatalog';
import useDashboardExchange from './dashboard/useDashboardExchange';
import useDashboardActionButtons from './dashboard/useDashboardActionButtons';
import useDashboardSession from './dashboard/useDashboardSession';
import useDashboardReturnExchange from './dashboard/useDashboardReturnExchange';
import useDashboardOrderFlow from './dashboard/useDashboardOrderFlow';
import { useGetGiftVoucherTypesQuery } from '../services/giftVoucherApi';
import { setGvBuyer, resetGvSale, selectGvSale } from '../store/giftVoucherSaleSlice';
import { useGiftVoucherDraftSync } from '../hooks/useGiftVoucherDraftSync';
import RecipientsDialog from '../components/pos/giftVoucher/RecipientsDialog';
import VoucherCheckoutPage from '../components/pos/giftVoucher/VoucherCheckoutPage';
import VoucherSalesDrawer from '../components/pos/giftVoucher/VoucherSalesDrawer';
import GiftVoucherLookupDialog from '../components/pos/giftVoucher/GiftVoucherLookupDialog';
import {
  GiftVoucherTypeCard, GiftVoucherSummaryCard, GiftVoucherButtons, GiftVoucherApprovals, GiftVoucherMobileTotal,
  VoucherDiscountDialog, useCollectBlock,
} from '../components/pos/giftVoucher/GiftVoucherSidePanel';
import GiftVoucherConfirmDialog from '../components/pos/giftVoucher/GiftVoucherConfirmDialog';

const ACTIVE_DRAFT_ORDER_ID_KEY = 'activeDraftOrderId';

export const buildDraftCreatePayload = ({ allItems, selectedCustomer, selectedSalesperson, notes }) => ({
  order_items_attributes: allItems,
  ...(selectedCustomer?.id ? { customer_id: selectedCustomer.id } : {}),
  ...(selectedSalesperson?.id ? { salesperson_id: selectedSalesperson.id } : {}),
  ...(notes ? { notes } : {}),
});

const formatCustomerAddress = (address) => {
  if (!address) return '—';
  if (typeof address === 'string') return address || '—';

  const formatted = [
    address.line1,
    address.line2,
    [address.city, address.state].filter(Boolean).join(', '),
    address.postal_code,
    address.country,
  ].filter(Boolean).join(', ');

  return formatted || '—';
};

const Dashboard = () => {
  const dispatch = useDispatch();
  const location = useLocation();
  const navigate = useNavigate();
  const searchInputRef = useRef(null);
  const modeSelectRef = useRef(null);
  const actionButtonRefs = useRef([]);
  const scanBuffer = useRef(''); // Ref for background barcode scanning
  
  const [billingMode, setBillingMode] = useState('billing');
  const [returnOrderInput, setReturnOrderInput] = useState('');
  const [lookedUpOrder, setLookedUpOrder] = useState(null);
  const [returnDrawerOpenDash, setReturnDrawerOpenDash] = useState(false);
  // Exchange mode — Dashboard-native flow
  const [exchangeSourceOrder, setExchangeSourceOrder] = useState(null);
  const [exchangeReturnItems, setExchangeReturnItems] = useState({}); // { [itemId]: { selected, quantity, reason_code_id } }
  const [exchangeNewItems, setExchangeNewItems] = useState([]);        // [{ product_id, product_name, sku, retail_price, quantity }]
  const [exchangeCheckoutOpen, setExchangeCheckoutOpen] = useState(false);
  // Exchange's own salesperson — kept apart from the normal bill's (Redux)
  // so picking one here never touches a bill in progress, or vice versa.
  // Keyed to the exchange bill it was picked for, so selecting another bill
  // (or cancel / complete, which clear the bill) starts empty again — the
  // cashier must pick one explicitly for every exchange.
  const [exchangeSalespersonPick, setExchangeSalespersonPick] = useState(null); // { orderId, sp: { id, name } }
  const [returnSearchResults, setReturnSearchResults] = useState([]); // customers from search
  const [returnOrderMatches, setReturnOrderMatches] = useState([]); // orders matched directly by order_number
  const [returnSearched, setReturnSearched] = useState(false);
  const [selectedReturnCustomer, setSelectedReturnCustomer] = useState(null);
  const [returnCustomerOrders, setReturnCustomerOrders] = useState([]);
  const [cart, setCartState] = useState([]);
  const [selectedProduct, setSelectedProduct] = useState(null);

  const [isCatalogView, setIsCatalogView] = useState(false);
  const [selectedTab, setSelectedTab] = useState(0);
  const [catalogFocusIndex, setCatalogFocusIndex] = useState(-1);
  const [catalogFocusZone, setCatalogFocusZone] = useState('tabs'); // 'tabs' | 'products'

  // Snackbar State
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  const { searchQuery } = useSelector((state) => state.productUI);
  const { store, organization, terminal } = useSelector((state) => state.auth);
  // Org switch (Gift Voucher Settings → "Enable on POS"). Off: no Gift Voucher mode,
  // balance check or tender — the POS looks exactly as it did before gift vouchers.
  const posGiftVouchersEnabled = Boolean(organization?.pos_gift_vouchers_enabled);
  // Cash Office replaces Cash Entry (Cash In / Cash Out) while the organization has it; the server refuses it too.
  const cashInOutEnabled = organization?.cash_in_out_enabled !== false && organization?.modules?.cash_office !== 'full';
  const {
    currentOrderId, status: orderStatus, cartItems: orderCartItems,
    grand_total, gross_subtotal, subtotal, discount_total, tax_total,
    selectedCustomer, selectedSalesperson,
    bill_discount_id, bill_discount_name, bill_discount_amount,
    applied_promotions, near_miss_hints,
    bill_promotion_name, bill_promotion_discount_amount, total_promo_savings,
  } = useSelector((state) => state.order);
  const { currentSession } = useSelector((state) => state.cashSession);

  const customerRequired = organization?.customer_required === true;
  const salespersonRequired = organization?.salesperson_required === true;
  const requireDenominationCounting = organization?.require_denomination_counting === true;
  const sessionBlocked   = !currentSession || currentSession?.status !== 'open';
  const sessionBlockedHint = 'Please open a new cash session';
  const checkoutBlocked  = cart.length === 0 || (customerRequired && !selectedCustomer)
    || (salespersonRequired && !selectedSalesperson) || sessionBlocked;
  const missingCustomerForCart = customerRequired && !selectedCustomer;
  const missingSalespersonForCart = salespersonRequired && !selectedSalesperson;
  const cartAddDisabled = billingMode === 'billing' && (missingCustomerForCart || missingSalespersonForCart);
  const cartAddDisabledHint = cartAddDisabled
    ? `Select ${[missingCustomerForCart && 'customer', missingSalespersonForCart && 'salesperson'].filter(Boolean).join(' and ')} before adding products`
    : '';
  const { data: customerDetailRes, isFetching: isCustomerDetailLoading } = useGetCustomerQuery(selectedCustomer?.id, {
    skip: !selectedCustomer?.id || billingMode !== 'billing',
  });
  const customerDetail = customerDetailRes?.data?.customer || customerDetailRes?.data || selectedCustomer;

  const showToast = useToast();
  const [createOrder] = useCreateOrderMutation();
  const [updateOrder] = useUpdateOrderMutation();
  const [cancelOrder] = useCancelOrderMutation();
  const [holdOrder] = useHoldOrderMutation();
  const [removeLoyalty] = useRemoveLoyaltyMutation();
  const [triggerScan] = useLazyScanProductQuery();
  const [triggerGetOrder] = useLazyGetOrderQuery();
  const [triggerSelectOrder, { isFetching: isSelectingOrder }] = useLazyGetOrderQuery();
  const [triggerCustomerSearch, { isFetching: isSearchingCustomers }] = useLazySearchCustomersQuery();
  const [triggerListReturnOrders, { isFetching: isLoadingCustomerOrders }] = useLazyListOrdersQuery();
  const [triggerListSessions] = useLazyListSessionsQuery();
  const [triggerGetSession] = useLazyGetSessionQuery();
  const [triggerHeldOrdersCheck] = useLazyListOrdersQuery();
  const [discardHeldOrders, { isLoading: isDiscardingHeldOrders }] = useDiscardHeldOrdersMutation();

  // Modal / Drawer visibility
  const [orderNotes, setOrderNotes] = useState('');

  const [isCustomerModalOpen, setIsCustomerModalOpen] = useState(false);
  const [isCustomerDetailModalOpen, setIsCustomerDetailModalOpen] = useState(false);
  const [isCheckoutOpen, setIsCheckoutOpen] = useState(false);
  const [isHoldDrawerOpen, setIsHoldDrawerOpen] = useState(false);
  const [isCarryBagModalOpen, setIsCarryBagModalOpen] = useState(false);
  const [isSalespersonDrawerOpen, setIsSalespersonDrawerOpen] = useState(false);
  const [isDiscountDrawerOpen, setIsDiscountDrawerOpen]       = useState(false);
  const [isPromotionsModalOpen, setIsPromotionsModalOpen]     = useState(false);
  const [nearMissDismissed, setNearMissDismissed]             = useState(false);
  const [billExpanded, setBillExpanded] = useState(false);
  const [denomBreakdownExpanded, setDenomBreakdownExpanded] = useState(false);
  const [searchHoverIndex, setSearchHoverIndex] = useState(-1);
  const [isOpenSessionModalOpen, setIsOpenSessionModalOpen]   = useState(false);
  const [isCloseSessionModalOpen, setIsCloseSessionModalOpen] = useState(false);
  const [isDiscardHeldPromptOpen, setIsDiscardHeldPromptOpen] = useState(false);
  const [heldBillsCount, setHeldBillsCount] = useState(0);
  const [isCashMovementModalOpen, setIsCashMovementModalOpen] = useState(false);
  const [isMobileScannerOpen, setIsMobileScannerOpen] = useState(false);
  const [isStockLookupOpen, setIsStockLookupOpen] = useState(false);
  // Gift Voucher mode (docs/ui-ux/gift-vouchers.md)
  const [gvBuyerPickerOpen, setGvBuyerPickerOpen] = useState(false);
  const [gvRecipients, setGvRecipients] = useState({ open: false, lineKey: null, tab: 'list' });
  const [gvDiscountOpen, setGvDiscountOpen] = useState(false);
  const [gvCollectOpen, setGvCollectOpen] = useState(false);
  const [gvSales, setGvSales] = useState({ open: false, q: '' });
  const [gvBalanceOpen, setGvBalanceOpen] = useState(false);
  // Sales Incentives (display only; hidden unless the org has the module in full).
  const { access: incentivesAccess, isIncentiveProduct, recheck: recheckIncentives } = useIncentiveProductSet();
  const [isIncentivesOpen, setIsIncentivesOpen] = useState(false);
  const closeIncentives = useCallback(() => setIsIncentivesOpen(false), []);
  const openIncentives = incentivesAccess ? () => setIsIncentivesOpen(true) : undefined;
  // Store Cash Office (granted module): the panel's button shows when the org has it and this cashier may view it
  // at this store; the badge counts cash on its way here.
  const cashOfficeAccess = organization?.modules?.cash_office || 'none';
  const [isCashOfficeOpen, setIsCashOfficeOpen] = useState(false);
  const { data: cashOfficeSummary, error: cashOfficeError } = useGetCashOfficeSummaryQuery(undefined, {
    skip: cashOfficeAccess === 'none', pollingInterval: 120000,
  });
  const cashOfficeVisible = cashOfficeAccess !== 'none' && cashOfficeError?.status !== 403;
  const openCashOffice = cashOfficeVisible ? () => setIsCashOfficeOpen(true) : undefined;
  const cashOfficeIncoming = cashOfficeSummary?.data?.incoming_transfers || 0;
  // A new bill (no current order) is a good moment to re-check the badge list (throttled; usually a 304).
  useEffect(() => { if (!currentOrderId) recheckIncentives(); }, [currentOrderId, recheckIncentives]);
  const [gvHoldConfirmOpen, setGvHoldConfirmOpen] = useState(false);
  const [gvDiscardOpen, setGvDiscardOpen] = useState(false);
  const [stockLookupPreselect, setStockLookupPreselect] = useState(null);
  const openStockLookup = useCallback((product = null) => {
    setStockLookupPreselect(product || null);
    setIsStockLookupOpen(true);
  }, []);
  const [isSessionInitializing, setIsSessionInitializing] = useState(true);

  // Carry bags + salespersons
  const { data: carryBagsData } = useGetCarryBagsQuery();
  const { data: salespersonsData } = useGetSalespersonsQuery();
  const carryBags = carryBagsData?.data || [];
  const salespersons = salespersonsData?.data || [];

  // Org settings (for exchange reason code requirement)
  const orgSettings = useMemo(() => resolveOrgSettings({}, organization || {}), [organization]);
  const { data: reasonData } = useGetReturnReasonCodesQuery(undefined, { skip: billingMode !== 'exchange' });
  const reasonCodes = useMemo(() => reasonData?.data?.return_reason_codes || [], [reasonData]);

  // Refs to avoid stale closures in callbacks
  const cartRef = useRef([]);
  const currentOrderIdRef = useRef(null);
  const orderCartItemsRef = useRef([]);
  const orderNotesRef = useRef('');
  const currentSessionRef = useRef(null);
  const hasRestoredDraftRef = useRef(false);
  const returnSearchTimerRef = useRef(null);

  // Writes cartRef synchronously on every cart change, in the same tick as the
  // call that changed it — not via a useLayoutEffect keyed on `cart`. Add-to-cart
  // and qty +/- read cartRef.current synchronously to compute the next cart; a
  // layout-effect-based sync only lands after React commits, so two clicks fired
  // before that commit (routine on POS touchscreens) would both read the same
  // stale snapshot and the second would silently overwrite/drop the first's item.
  const setCart = useCallback((next) => {
    const value = typeof next === 'function' ? next(cartRef.current) : next;
    cartRef.current = value;
    setCartState(value);
  }, []);

  useEffect(() => { currentOrderIdRef.current = currentOrderId; }, [currentOrderId]);
  useEffect(() => { orderCartItemsRef.current = orderCartItems; }, [orderCartItems]);
  useEffect(() => { orderNotesRef.current = orderNotes; }, [orderNotes]);
  useEffect(() => { currentSessionRef.current = currentSession; }, [currentSession]);
  useEffect(() => {
    if (currentOrderId && orderStatus === 'draft') {
      sessionStorage.setItem(ACTIVE_DRAFT_ORDER_ID_KEY, String(currentOrderId));
      return;
    }
    // On first load, keep the session-scoped draft key intact until restore logic runs.
    if (!hasRestoredDraftRef.current && !currentOrderId && !orderStatus) {
      return;
    }
    sessionStorage.removeItem(ACTIVE_DRAFT_ORDER_ID_KEY);
  }, [currentOrderId, orderStatus]);

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const isCameraScannerDevice = useMediaQuery(theme.breakpoints.down('lg'));
  const { openMobileSidebar } = useContext(SidebarContext);

  const [checkMe] = useLazyGetMeQuery();

  // Restore pending discount approval from localStorage on mount (survives page
  // refresh). setPendingApproval always sets awaitingInline: true (that's its
  // contract — a fresh apply always opens DiscountApprovalModal) — if the
  // persisted state says the cashier had already handed off to the fallback
  // banner (awaitingInline: false) before the refresh, immediately follow up
  // with handOffPendingApproval() so the refresh restores into the SAME
  // surface (banner, not modal) instead of always reopening the modal.
  useEffect(() => {
    const stored = localStorage.getItem('discountApproval');
    if (stored) {
      try {
        const parsed = JSON.parse(stored);
        if (parsed.requestId) {
          dispatch(setPendingApproval(parsed));
          if (!parsed.awaitingInline) dispatch(handOffPendingApproval());
        }
      } catch (_) {}
    }
  }, []); // eslint-disable-line

  // Mirrors discountSlice's pending-approval fields to localStorage on every
  // change — the single source of truth for the restore effect above. Central
  // (not scattered `setItem` calls at each dispatch site) so it can't be missed
  // when a new call site is added, and self-clears the instant
  // pendingApprovalRequestId goes null (covers clearPendingApproval()).
  const {
    pendingApprovalRequestId, pendingApprovalDiscountName, pendingApprovalLevel,
    pendingOrderId, awaitingInline,
  } = useSelector((s) => s.discount);
  useEffect(() => {
    if (pendingApprovalRequestId) {
      localStorage.setItem('discountApproval', JSON.stringify({
        requestId: pendingApprovalRequestId,
        discountName: pendingApprovalDiscountName,
        level: pendingApprovalLevel,
        orderId: pendingOrderId,
        awaitingInline,
      }));
    } else {
      localStorage.removeItem('discountApproval');
    }
  }, [ pendingApprovalRequestId, pendingApprovalDiscountName, pendingApprovalLevel, pendingOrderId, awaitingInline ]);

  // Callback passed to DiscountDrawer — clears local cart state when approval is pending (D1)
  const handlePendingApproval = useCallback(() => {
    setCart([]);
    setSelectedProduct(null);
    setOrderNotes('');
  }, []);

  useDashboardSession({
    ACTIVE_DRAFT_ORDER_ID_KEY,
    checkMe,
    currentOrderIdRef,
    cartRef,
    currentSession,
    currentSessionRef,
    dispatch,
    hasRestoredDraftRef,
    isSessionInitializing,
    setCart,
    setIsOpenSessionModalOpen,
    setIsSessionInitializing,
    setOrderNotes,
    setSelectedProduct,
    showToast,
    terminalId: terminal?.id,
    triggerGetOrder,
    triggerGetSession,
    triggerListSessions,
  });

  const {
    adjustQty,
    displayTotal,
    handleAddCarryBag,
    handleAddToCart,
    handleDiscard,
    handleHoldBill,
    handleNewSale,
    handleNotesBlur,
    handlePaymentCompleted,
    handleRemoveCustomer,
    handleRemoveOrderSalesperson,
    handleRemoveItemSalesperson,
    handleResume,
    removeFromCart,
    updateQty,
  } = useDashboardOrderFlow({
    buildDraftCreatePayload,
    billingMode,
    cancelOrder,
    cart,
    cartRef,
    createOrder,
    currentOrderIdRef,
    customerRequired,
    dispatch,
    exchangeSourceOrder,
    grand_total,
    holdOrder,
    orderCartItemsRef,
    orderNotesRef,
    salespersonRequired,
    selectedCustomer,
    selectedProduct,
    selectedSalesperson,
    setCart,
    setExchangeNewItems,
    setIsCheckoutOpen,
    setIsCustomerModalOpen,
    setIsHoldDrawerOpen,
    setIsSalespersonDrawerOpen,
    setOrderNotes,
    setSelectedProduct,
    setSnackbar,
    showToast,
    triggerGetOrder,
    updateOrder,
    removeLoyalty,
  });

  const [inStockOnly, setInStockOnly] = useState(false);

  const {
    categories,
    isLoadingCategories,
    activeCategory,
    productsToShow,
    isLoadingCategoryProducts,
    categoryHasMore,
    isFetchingMoreCategoryProducts,
    loadMoreCategoryProducts,
    searchResults,
    isSearchingProducts,
    searchHasMore,
    isFetchingMoreResults,
    loadMoreSearchResults,
    calculateItemTax,
    selectedTaxInfo,
    getProductStock,
  } = useDashboardCatalog({
    selectedTab,
    searchQuery,
    orderCartItems,
    selectedProduct,
    inStockOnly,
  });

  const {
    handleBillingModeChange,
    handleSelectExchangeOrder,
    handleCancelExchange,
    handleAddExchangeNewItem,
    handleReturnSearch,
    handleSelectReturnCustomer,
    handleSelectReturnOrder,
  } = useDashboardReturnExchange({
    dispatch,
    exchangeNewItems,
    location,
    navigate,
    returnOrderInput,
    returnSearchTimerRef,
    setBillingMode,
    setExchangeCheckoutOpen,
    setExchangeNewItems,
    setExchangeReturnItems,
    setExchangeSourceOrder,
    setLookedUpOrder,
    setReturnCustomerOrders,
    setReturnDrawerOpenDash,
    setReturnOrderInput,
    setReturnOrderMatches,
    setReturnSearchResults,
    setReturnSearched,
    setSearchHoverIndex,
    setSelectedReturnCustomer,
    showToast,
    triggerCustomerSearch,
    triggerListReturnOrders,
    triggerSelectOrder,
  });

  // Reset dropdown highlight when search results change
  useEffect(() => { setSearchHoverIndex(-1); }, [searchResults]);

  // Re-show near-miss bar only when the set of hint IDs actually changes (not on every re-render)
  const nearMissKey = near_miss_hints.map((h) => h.promotion_id).join(',');
  useEffect(() => { if (near_miss_hints.length > 0) setNearMissDismissed(false); }, [nearMissKey]);

  // Global Keyboard Listener — F-keys, search dropdown nav, barcode scan, cart nav
  useEffect(() => {
    const handleKeyDown = (e) => {
      const anyModalOpen =
        gvBuyerPickerOpen || gvRecipients.open || gvDiscountOpen || gvCollectOpen || gvSales.open || gvBalanceOpen || gvHoldConfirmOpen || gvDiscardOpen ||
        isCustomerModalOpen ||
        isCheckoutOpen ||
        isHoldDrawerOpen ||
        isIncentivesOpen ||
        isCarryBagModalOpen ||
        isSalespersonDrawerOpen ||
        isPromotionsModalOpen ||
        isDiscountDrawerOpen ||
        isOpenSessionModalOpen ||
        isCloseSessionModalOpen ||
        isCashMovementModalOpen ||
        isCashOfficeOpen ||
        isMobileScannerOpen ||
        returnDrawerOpenDash;
      const hotkeysBlocked = anyModalOpen || isSessionInitializing || sessionBlocked || (billingMode === 'exchange' && !exchangeSourceOrder);

      if (/^F\d{1,2}$/.test(e.key) && hotkeysBlocked) {
        e.preventDefault();
        return;
      }
      if (hotkeysBlocked && billingMode !== 'return') return;

      // Gift Voucher mode: only F2 (pick buyer) applies; billing F-keys have no bill to act on.
      if (billingMode === 'gift_voucher') {
        if (e.key === 'F2') { e.preventDefault(); setGvBuyerPickerOpen(true); return; }
        if (/^F\d{1,2}$/.test(e.key)) e.preventDefault();
        return;
      }

      // ── F-keys (always fire) ─────────────────────────────────────────────
      const inExchangeMode = billingMode === 'exchange';
      const inReturnMode = billingMode === 'return';
      if (/^F\d{1,2}$/.test(e.key) && inReturnMode) {
        e.preventDefault();
        return;
      }
      if (e.key === 'F1') { e.preventDefault(); searchInputRef.current?.focus(); return; }
      if (e.key === 'F2') { e.preventDefault(); if (!inExchangeMode) setIsCustomerModalOpen(true); return; }
      if (e.key === 'F3') { e.preventDefault(); setIsCatalogView(p => !p); setCatalogFocusIndex(-1); setCatalogFocusZone('tabs'); return; }
      if (e.key === 'F4') { e.preventDefault(); if (!inExchangeMode) handleHoldBill(); return; }
      if (e.key === 'F5') { e.preventDefault(); if (!inExchangeMode) setIsHoldDrawerOpen(true); return; }
      if (e.key === 'F6') { e.preventDefault(); setIsSalespersonDrawerOpen(true); return; }
      if (e.key === 'F7') { e.preventDefault(); if (!checkoutBlocked) setIsCheckoutOpen(true); return; }
      if (e.key === 'F8') { e.preventDefault(); if (!inExchangeMode && carryBags.length > 0) setIsCarryBagModalOpen(true); return; }
      if (e.key === 'F9') { e.preventDefault(); if (!inExchangeMode) setIsPromotionsModalOpen(true); return; }

      // Block everything else while a modal/drawer is open
      if (anyModalOpen) return;

      if (inReturnMode) {
        // In return mode keep only return-order search / barcode scan active.
        // Product search, cart navigation, and billing shortcuts are intentionally disabled.
        if (e.key === 'Enter') {
          const barcode = scanBuffer.current.trim();
          if (barcode) {
            setReturnOrderInput(barcode);
            scanBuffer.current = '';
            setTimeout(() => handleReturnSearch(), 50);
          }
          return;
        }
        if (typeof e.key === 'string' && e.key.length === 1 && !e.ctrlKey && !e.altKey && !e.metaKey) {
          scanBuffer.current += e.key;
        }
        return;
      }

      // Skip if typing in a non-search input (other drawers/forms)
      const inOtherInput = (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA') && e.target !== searchInputRef.current;
      if (inOtherInput) return;

      // ── Action grid navigation (when a grid button has keyboard focus) ───
      // Only takes over arrows in the "nothing else to do" state — once the
      // catalog is open or the cart has items, arrows drive those instead.
      const focusedBtnIndex = actionButtonRefs.current.indexOf(document.activeElement);
      if (focusedBtnIndex >= 0 && cart.length === 0 && !isCatalogView && /^Arrow(Up|Down|Left|Right)$|^( |Enter)$/.test(e.key)) {
        e.preventDefault();
        const col = focusedBtnIndex % 4;
        const row = Math.floor(focusedBtnIndex / 4);
        if (e.key === 'ArrowLeft') actionButtonRefs.current[row * 4 + (col + 3) % 4]?.focus();
        else if (e.key === 'ArrowRight') actionButtonRefs.current[row * 4 + (col + 1) % 4]?.focus();
        else if (e.key === 'ArrowUp') {
          if (row === 0) searchInputRef.current?.focus();
          else actionButtonRefs.current[col]?.focus();
        } else if (e.key === 'ArrowDown') {
          if (row === 1) actionButtonRefs.current[col]?.focus();
          else actionButtonRefs.current[4 + col]?.focus();
        } else {
          document.activeElement?.click();
        }
        return;
      }

      // ── Search dropdown navigation ───────────────────────────────────────
      if (searchResults.length > 0) {
        if (e.key === 'ArrowDown') {
          e.preventDefault();
          setSearchHoverIndex(i => (i + 1) % searchResults.length);
          return;
        }
        if (e.key === 'ArrowUp') {
          e.preventDefault();
          setSearchHoverIndex(i => (i - 1 + searchResults.length) % searchResults.length);
          return;
        }
        if (e.key === 'Enter') {
          e.preventDefault();
          const target = searchResults[searchHoverIndex] ?? searchResults[0];
          if (target) handleAddToCart(target);
          setSearchHoverIndex(-1);
          scanBuffer.current = '';
          return;
        }
        if (e.key === 'Escape') {
          e.preventDefault();
          dispatch(setProductSearch(''));
          setSearchHoverIndex(-1);
          return;
        }
      }

      // ── Top-zone navigation: search box → mode dropdown / action grid ────
      if (e.target === searchInputRef.current) {
        if (e.key === 'ArrowRight' && !searchQuery) {
          e.preventDefault();
          modeSelectRef.current?.focus();
          return;
        }
        if (e.key === 'ArrowDown' && searchResults.length === 0 && cart.length === 0 && !isCatalogView) {
          e.preventDefault();
          actionButtonRefs.current[0]?.focus();
          return;
        }
      }

      // ── Catalog navigation (category tabs + product list, F3 view) ───────
      if (isCatalogView) {
        const inSearchInput = e.target === searchInputRef.current;

        if (catalogFocusZone === 'tabs' && categories.length > 0) {
          if (e.key === 'ArrowDown') {
            e.preventDefault();
            setSelectedTab(t => (t + 1) % categories.length);
            return;
          }
          if (e.key === 'ArrowUp') {
            e.preventDefault();
            setSelectedTab(t => (t - 1 + categories.length) % categories.length);
            return;
          }
          if (!inSearchInput && e.key === 'ArrowRight' && productsToShow.length > 0) {
            e.preventDefault();
            setCatalogFocusZone('products');
            setCatalogFocusIndex(0);
            return;
          }
        }

        if (catalogFocusZone === 'products' && productsToShow.length > 0) {
          if (e.key === 'ArrowDown') {
            e.preventDefault();
            setCatalogFocusIndex(i => (i + 1 >= productsToShow.length ? 0 : i + 1));
            return;
          }
          if (e.key === 'ArrowUp') {
            e.preventDefault();
            setCatalogFocusIndex(i => (i <= 0 ? productsToShow.length - 1 : i - 1));
            return;
          }
          if (!inSearchInput && e.key === 'ArrowLeft') {
            e.preventDefault();
            setCatalogFocusZone('tabs');
            setCatalogFocusIndex(-1);
            return;
          }
          if (catalogFocusIndex >= 0 && (e.key === 'Enter' || (e.key === ' ' && !inSearchInput))) {
            e.preventDefault();
            const product = productsToShow[catalogFocusIndex];
            if (product) {
              if (billingMode === 'exchange' && exchangeSourceOrder) handleAddExchangeNewItem(product);
              else handleAddToCart(product);
              if (isMobile) setIsCatalogView(false);
            }
            return;
          }
        }
      }

      // ── Barcode scan buffer ──────────────────────────────────────────────
      if (e.key === 'Enter') {
        const barcode = scanBuffer.current.trim();
        if (barcode) {
          if (billingMode === 'exchange' && exchangeSourceOrder) {
            // In exchange workflow (order already selected), scan adds a new
            // product. Always resolved live against /products/scan/:code —
            // an exact, tenant-scoped, already-fast lookup — rather than a
            // local cache first, so the stock it adds is never stale.
            triggerScan(barcode).then(({ data, error }) => {
              if (data?.data) handleAddExchangeNewItem(data.data);
              // Backend returns 422 "Product is inactive and cannot be sold" for
              // deactivated products, 404 "Product not found" otherwise — surface
              // whichever message it sends instead of a blanket "not found".
              else setSnackbar({ open: true, message: error?.data?.error || 'Product not found for barcode scan', severity: error ? 'error' : 'warning' });
            });
            scanBuffer.current = '';
            return;
          }
          if (billingMode === 'return' || billingMode === 'exchange') {
            // In return/exchange order search mode, scan fills the search input and triggers order search
            setReturnOrderInput(barcode);
            scanBuffer.current = '';
            setTimeout(() => handleReturnSearch(), 50);
            return;
          }
          triggerScan(barcode).then(({ data, error }) => {
            if (data?.data) handleAddToCart(data.data, true);
            // Backend returns 422 "Product is inactive and cannot be sold" for
            // deactivated products, 404 "Product not found" otherwise.
            else setSnackbar({ open: true, message: error?.data?.error || 'Product not found', severity: error ? 'error' : 'warning' });
          });
        }
        scanBuffer.current = '';
        return;
      }
      if (typeof e.key === 'string' && e.key.length === 1 && !e.ctrlKey && !e.altKey && !e.metaKey) {
        scanBuffer.current += e.key;
      }

      // ── Cart navigation & qty (dropdown closed) ──────────────────────────
      if (cart.length === 0) return;
      const currentIndex = cart.findIndex(item => item.id === selectedProduct?.id);
      if (e.key === 'ArrowDown') { e.preventDefault(); setSelectedProduct(cart[(currentIndex + 1) % cart.length]); }
      else if (e.key === 'ArrowUp') { e.preventDefault(); setSelectedProduct(cart[(currentIndex - 1 + cart.length) % cart.length]); }
      if (selectedProduct) {
        if ((e.key === '+' || e.key === '=') && e.target !== searchInputRef.current) { e.preventDefault(); adjustQty(selectedProduct.id, 1); }
        else if (e.key === '-' && e.target !== searchInputRef.current) { e.preventDefault(); adjustQty(selectedProduct.id, -1); }
        else if ((e.key === 'Delete' || e.key === 'Backspace') && e.target !== searchInputRef.current) {
          e.preventDefault();
          const removeIndex = cart.findIndex(item => item.id === selectedProduct.id);
          const remaining = cart.filter(item => item.id !== selectedProduct.id);
          removeFromCart(selectedProduct.id);
          setSelectedProduct(remaining.length > 0 ? remaining[Math.min(removeIndex, remaining.length - 1)] : null);
        }
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [cart, selectedProduct, searchResults, searchHoverIndex, adjustQty,
      handleAddToCart, handleHoldBill, triggerScan, dispatch, checkoutBlocked, carryBags,
      isCustomerModalOpen, isCheckoutOpen, isHoldDrawerOpen, isIncentivesOpen, isCarryBagModalOpen,
      isSalespersonDrawerOpen, isPromotionsModalOpen, isDiscountDrawerOpen,
      isOpenSessionModalOpen, isCloseSessionModalOpen, isCashMovementModalOpen, isCashOfficeOpen, isMobileScannerOpen,
      returnDrawerOpenDash, isSessionInitializing, sessionBlocked,
      billingMode, handleReturnSearch, exchangeSourceOrder, handleAddExchangeNewItem,
      isCatalogView, categories, productsToShow, catalogFocusIndex, catalogFocusZone, isMobile,
      removeFromCart, searchQuery,
      gvBuyerPickerOpen, gvRecipients.open, gvDiscountOpen, gvCollectOpen, gvSales.open, gvBalanceOpen, gvHoldConfirmOpen, gvDiscardOpen]);

  const exchangeSourceOrderId = exchangeSourceOrder?.id;
  const exchangeSalesperson =
    exchangeSalespersonPick && exchangeSalespersonPick.orderId === exchangeSourceOrderId
      ? exchangeSalespersonPick.sp
      : null;
  const setExchangeSalesperson = useCallback(
    (sp) => setExchangeSalespersonPick(sp ? { orderId: exchangeSourceOrderId, sp } : null),
    [exchangeSourceOrderId]
  );

  // Customer on an exchange is always the original bill's — shown, never changed.
  const exchangeCustomer = useMemo(
    () => (exchangeSourceOrder && exchangeSourceOrder.customer_id
      ? { id: exchangeSourceOrder.customer_id, name: exchangeSourceOrder.customer_name }
      : null),
    [exchangeSourceOrder]
  );

  const disableActionShortcuts = isSessionInitializing || sessionBlocked || isOpenSessionModalOpen || billingMode === 'return' || (billingMode === 'exchange' && !exchangeSourceOrder);
  const {
    inExchangeWorkflow,
    exchangeReturnTotal,
    exchangeNewTotal,
    exchangeNetAmount,
    exchangeDirection,
    exchangeReturnItemsPayload,
    exchangeNewItemsPayload,
    allReturnItemsHaveReasonCode,
    canCompleteExchange,
    exchangeBlockReason,
    nearMissHints: exchangeNearMissHints,
    appliedPromotions: exchangeAppliedPromotions,
  } = useDashboardExchange({
    billingMode,
    exchangeSourceOrder,
    exchangeReturnItems,
    exchangeNewItems,
    exchangeSalesperson,
    customerRequired,
    salespersonRequired,
    orgSettings,
    reasonCodes,
  });

  const handleCameraScan = useCallback(async (rawCode) => {
    const code = String(rawCode || '').trim();
    if (!code) return { success: false, message: 'Scanned code was empty.' };

    // Always resolved live against /products/scan/:code — see the keyboard
    // scan-buffer handler above for why there's no local-cache-first check.
    const res = await triggerScan(code);
    const product = res?.data?.data;
    if (!product) {
      return { success: false, message: `No product found for "${code}".` };
    }

    if (inExchangeWorkflow) handleAddExchangeNewItem(product);
    else handleAddToCart(product, true);

    return { success: true, message: `Added: ${product.name}` };
  }, [handleAddExchangeNewItem, handleAddToCart, inExchangeWorkflow, triggerScan]);

  // Gate: closing the cash session is blocked while this terminal still has
  // bills on hold (a store can have several terminals, each with its own).
  // Skip straight to the real close-session flow when there are none.
  const requestCloseSession = useCallback(async () => {
    if (!terminal?.id) {
      setIsCloseSessionModalOpen(true);
      return;
    }
    try {
      const res = await triggerHeldOrdersCheck({ status: 'on_hold', terminal_id: terminal.id, per_page: 1 }).unwrap();
      const count = res?.data?.meta?.total_count ?? (res?.data?.orders?.length || 0);
      if (count > 0) {
        setHeldBillsCount(count);
        setIsDiscardHeldPromptOpen(true);
      } else {
        setIsCloseSessionModalOpen(true);
      }
    } catch (err) {
      showToast('Could not check held bills. Please try again.', 'error');
    }
  }, [terminal, triggerHeldOrdersCheck, showToast]);

  const handleDiscardAllHeld = async () => {
    try {
      await discardHeldOrders().unwrap();
      showToast('Held bills discarded', 'success');
      setIsDiscardHeldPromptOpen(false);
      setIsCloseSessionModalOpen(true);
    } catch (err) {
      showToast(err?.data?.error || 'Failed to discard held bills. Please try again.', 'error');
    }
  };

  // ── Gift Voucher mode ────────────────────────────────────────────────────
  const inGiftVoucherMode = billingMode === 'gift_voucher';
  const { data: gvTypesData, isLoading: isLoadingGvTypes } = useGetGiftVoucherTypesQuery(undefined, { skip: !inGiftVoucherMode });
  const gvTypes = useMemo(() => gvTypesData?.data?.gift_voucher_types || [], [gvTypesData]);
  useGiftVoucherDraftSync({ active: inGiftVoucherMode, types: gvTypes });
  const gvSale = useSelector(selectGvSale);
  const gvCollectBlock = useCollectBlock(gvTypes, sessionBlocked);
  const gvMaxDiscount = useMemo(() => {
    const limits = gvSale.lines.map((l) => gvTypes.find((t) => t.id === l.typeId)).filter(Boolean).map((t) => Number(t.max_sale_discount_percent));
    return limits.length ? Math.min(...limits) : null;
  }, [gvSale.lines, gvTypes]);

  // Switching into Gift Voucher mode with items on the bill holds the bill first.
  const requestBillingModeChange = useCallback((mode) => {
    if (mode === 'gift_voucher' && !posGiftVouchersEnabled) return;
    if (mode === 'gift_voucher' && billingMode !== 'gift_voucher' && cart.length > 0) {
      setGvHoldConfirmOpen(true);
      return;
    }
    handleBillingModeChange(mode);
  }, [billingMode, cart.length, handleBillingModeChange, posGiftVouchersEnabled]);

  const confirmHoldAndSwitch = async () => {
    const held = await handleHoldBill({ openDrawer: false });
    setGvHoldConfirmOpen(false);
    if (held) {
      handleBillingModeChange('gift_voucher');
      showToast('Bill held · resume it from Held (F5)', 'info');
    }
  };

  const gvActionButtons = useMemo(() => [
    { label: 'Buyer', shortcut: 'F2', icon: <PersonAdd fontSize="small" />, color: gvSale.buyer ? '#10B981' : '#EF4444', required: !gvSale.buyer, onClick: () => setGvBuyerPickerOpen(true), disabled: sessionBlocked },
    { label: 'Upload CSV', shortcut: null, icon: <UploadFile fontSize="small" />, color: '#4F46E5', onClick: () => setGvRecipients({ open: true, lineKey: null, tab: 'csv' }), disabled: sessionBlocked || !gvTypes.length },
    { label: 'Discount', shortcut: null, icon: <LocalOffer fontSize="small" />, color: gvSale.discount ? '#16A34A' : (gvSale.lines.length ? '#4F46E5' : '#CBD5E1'), onClick: () => setGvDiscountOpen(true), disabled: sessionBlocked || !gvSale.lines.length },
    { label: 'Past sales', shortcut: null, icon: <ManageSearch fontSize="small" />, color: '#DB2777', onClick: () => setGvSales({ open: true, q: '' }), disabled: false },
  ], [gvSale.buyer, gvSale.discount, gvSale.lines.length, gvTypes.length, sessionBlocked]);

  const gvButtons = (height) => (
    <GiftVoucherButtons block={gvCollectBlock} height={height} onDiscard={() => setGvDiscardOpen(true)} onCollect={() => setGvCollectOpen(true)} />
  );
  const gvRightSlots = inGiftVoucherMode ? {
    center: <GiftVoucherTypeCard types={gvTypes} />,
    summary: <GiftVoucherSummaryCard />,
    buttons: gvButtons(52),
  } : null;
  const gvMobileSlots = inGiftVoucherMode ? {
    // Same rule as GiftVoucherApprovals: keep an approved banner after the server drops the kind.
    approvals: (gvSale.approvalsRequired.length || Object.values(gvSale.approvalRequests).some((r) => r?.status === 'approved'))
      ? <GiftVoucherApprovals compact /> : null,
    total: <GiftVoucherMobileTotal block={gvCollectBlock} />,
    buttons: gvButtons(48),
  } : null;

  const actionButtons = useDashboardActionButtons({
    customerRequired,
    selectedCustomer: inExchangeWorkflow ? exchangeCustomer : selectedCustomer,
    setIsCustomerModalOpen,
    handleHoldBill,
    disableActionShortcuts,
    inExchangeWorkflow,
    carryBags,
    setIsCarryBagModalOpen,
    bill_discount_id,
    orderCartItems,
    currentOrderId,
    cart,
    setIsDiscountDrawerOpen,
    setIsHoldDrawerOpen,
    isCatalogView,
    setIsCatalogView,
    setCatalogFocusIndex,
    setCatalogFocusZone,
    salespersonRequired,
    selectedSalesperson: inExchangeWorkflow ? exchangeSalesperson : selectedSalesperson,
    setIsSalespersonDrawerOpen,
    applied_promotions,
    setIsPromotionsModalOpen,
    inReturnMode: billingMode === 'return',
  });

  const scrollbarStyle = {
    scrollbarWidth: 'thin',
    scrollbarColor: '#CBD5E1 #F1F5F9',
    '&::-webkit-scrollbar': { width: '4px', height: '4px' },
    '&::-webkit-scrollbar-track': { background: '#F1F5F9' },
    '&::-webkit-scrollbar-thumb': { background: '#CBD5E1', borderRadius: '10px' },
    '&::-webkit-scrollbar-thumb:hover': { background: '#94A3B8' },
    // Chrome/Windows still renders the native increment/decrement scrollbar
    // buttons even with a custom thumb/track — at this 4px width their hit
    // target is too small and sits inside the container's rounded corner,
    // so clicking them doesn't reliably scroll. Thumb-drag and wheel/trackpad
    // scrolling are unaffected; just remove the broken button affordance.
    '&::-webkit-scrollbar-button': { display: 'none', width: 0, height: 0 },
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', width: '100%', bgcolor: '#F1F5F9', overflow: 'hidden' }}>

      <CarryBagModal
        open={isCarryBagModalOpen}
        onClose={() => setIsCarryBagModalOpen(false)}
        carryBags={carryBags}
        onAdd={handleAddCarryBag}
      />

      <SalespersonDrawer
        open={isSalespersonDrawerOpen}
        onClose={() => setIsSalespersonDrawerOpen(false)}
        salespersons={salespersons}
        {...(inExchangeWorkflow ? { currentSalesperson: exchangeSalesperson, onSelect: setExchangeSalesperson } : {})}
      />

      <DiscountDrawer
        open={isDiscountDrawerOpen}
        onClose={() => setIsDiscountDrawerOpen(false)}
      />

      <DiscountApprovalModal onPendingApproval={handlePendingApproval} />

      <PromotionsModal
        open={isPromotionsModalOpen}
        onClose={() => setIsPromotionsModalOpen(false)}
      />

      <CustomerSearchModal
        open={isCustomerModalOpen}
        onClose={() => setIsCustomerModalOpen(false)}
      />

      <DashboardCustomerDetailDialog
        open={isCustomerDetailModalOpen}
        selectedCustomer={selectedCustomer}
        isCustomerDetailLoading={isCustomerDetailLoading}
        customerDetail={customerDetail}
        formatCustomerAddress={formatCustomerAddress}
        onClose={() => setIsCustomerDetailModalOpen(false)}
        onChange={() => {
          setIsCustomerDetailModalOpen(false);
          setIsCustomerModalOpen(true);
        }}
        onRemove={() => {
          setIsCustomerDetailModalOpen(false);
          handleRemoveCustomer();
        }}
      />

      <CheckoutPage
        open={isCheckoutOpen}
        onClose={() => setIsCheckoutOpen(false)}
        onNewSale={handleNewSale}
        onPaymentCompleted={handlePaymentCompleted}
        cart={cart}
        onTagSalesperson={incentivesAccess ? () => { setIsCheckoutOpen(false); setIsSalespersonDrawerOpen(true); } : undefined}
      />

      <IncentivesDrawer open={isIncentivesOpen} onClose={closeIncentives} />

      <HoldOrdersDrawer
        open={isHoldDrawerOpen}
        onClose={() => setIsHoldDrawerOpen(false)}
        onResume={handleResume}
        terminalId={terminal?.id}
      />

      {requireDenominationCounting ? (
        <CashDenominationSessionModal
          mode="open"
          open={isOpenSessionModalOpen}
          store={store}
          terminal={terminal}
          onSessionOpened={(session) => {
            dispatch(setCurrentSession(session));
            setIsOpenSessionModalOpen(false);
          }}
        />
      ) : (
        <OpenSessionModal
          open={isOpenSessionModalOpen}
          store={store}
          terminal={terminal}
          onSessionOpened={(session) => {
            dispatch(setCurrentSession(session));
            setIsOpenSessionModalOpen(false);
          }}
        />
      )}

      <DiscardHeldBillsDialog
        open={isDiscardHeldPromptOpen}
        heldCount={heldBillsCount}
        loading={isDiscardingHeldOrders}
        onCancel={() => setIsDiscardHeldPromptOpen(false)}
        onDiscardAll={handleDiscardAllHeld}
      />

      {requireDenominationCounting ? (
        <CashDenominationSessionModal
          mode="close"
          open={isCloseSessionModalOpen}
          onClose={() => setIsCloseSessionModalOpen(false)}
          session={currentSession}
          onSessionClosed={(session) => {
            dispatch(setCurrentSession(session));
            setIsCloseSessionModalOpen(false);
          }}
        />
      ) : (
        <CloseSessionModal
          open={isCloseSessionModalOpen}
          onClose={() => setIsCloseSessionModalOpen(false)}
          session={currentSession}
          onSessionClosed={(session) => {
            dispatch(setCurrentSession(session));
            setIsCloseSessionModalOpen(false);
          }}
        />
      )}

      <CashMovementModal
        open={isCashMovementModalOpen}
        onClose={() => setIsCashMovementModalOpen(false)}
        session={currentSession}
      />

      {cashOfficeVisible && (
        <CashOfficeModal
          open={isCashOfficeOpen}
          onClose={() => setIsCashOfficeOpen(false)}
          session={currentSession}
          readOnly={cashOfficeAccess === 'read_only'}
        />
      )}

      <MobileScannerModal
        open={isMobileScannerOpen}
        onClose={() => setIsMobileScannerOpen(false)}
        onDetected={handleCameraScan}
      />

      {/* ── Gift Voucher mode ── */}
      <CustomerSearchModal
        open={gvBuyerPickerOpen}
        onClose={() => setGvBuyerPickerOpen(false)}
        onSelectCustomer={(c) => dispatch(setGvBuyer({ id: c.id, name: c.name, phone_number: c.phone_number, email: c.email || '' }))}
      />
      <RecipientsDialog
        open={gvRecipients.open}
        lineKey={gvRecipients.lineKey}
        startTab={gvRecipients.tab}
        types={gvTypes}
        onClose={() => setGvRecipients({ open: false, lineKey: null, tab: 'list' })}
      />
      <VoucherDiscountDialog open={gvDiscountOpen} onClose={() => setGvDiscountOpen(false)} maxPercent={gvMaxDiscount} />
      <VoucherCheckoutPage
        open={gvCollectOpen}
        onClose={() => setGvCollectOpen(false)}
      />
      <VoucherSalesDrawer open={gvSales.open} initialQuery={gvSales.q} onClose={() => setGvSales({ open: false, q: '' })} />
      <GiftVoucherLookupDialog open={gvBalanceOpen} mode="balance" onClose={() => setGvBalanceOpen(false)} />
      <GiftVoucherConfirmDialog
        open={gvHoldConfirmOpen}
        tone="info"
        title="Hold this bill first?"
        message={`The current bill (${cart.length} item${cart.length !== 1 ? 's' : ''}) will be held. Resume it any time from Held (F5).`}
        confirmLabel="Hold bill & continue"
        onCancel={() => setGvHoldConfirmOpen(false)}
        onConfirm={confirmHoldAndSwitch}
      />
      <GiftVoucherConfirmDialog
        open={gvDiscardOpen}
        tone="danger"
        title="Discard this voucher sale?"
        message="The draft and its lines will be removed. Nothing has been charged."
        confirmLabel="Discard"
        onCancel={() => setGvDiscardOpen(false)}
        onConfirm={() => { dispatch(resetGvSale()); setGvDiscardOpen(false); showToast('Voucher sale discarded', 'info'); }}
      />

      <StockLookupModal
        open={isStockLookupOpen}
        onClose={() => setIsStockLookupOpen(false)}
        initialProduct={stockLookupPreselect}
      />

      <Snackbar
        open={snackbar.open}
        autoHideDuration={1000}
        onClose={() => setSnackbar(s => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert severity={snackbar.severity} variant="filled" sx={{ width: '100%', borderRadius: '8px' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>

      {/* ── Main content area ── */}
      <Box sx={{ display: 'flex', flexGrow: 1, minHeight: 0, flexDirection: { xs: 'column', md: 'row' }, overflow: 'hidden' }}>

        <DashboardLeftPanel
          isMobile={isMobile}
          isCameraScannerDevice={isCameraScannerDevice}
          openMobileSidebar={openMobileSidebar}
          onOpenMobileScanner={() => setIsMobileScannerOpen(true)}
          sessionBlocked={sessionBlocked}
          sessionBlockedHint={sessionBlockedHint}
          cartAddDisabled={cartAddDisabled}
          cartAddDisabledHint={cartAddDisabledHint}
          billingMode={billingMode}
          returnOrderInput={returnOrderInput}
          setReturnOrderInput={setReturnOrderInput}
          returnSearchTimerRef={returnSearchTimerRef}
          handleReturnSearch={handleReturnSearch}
          setReturnSearchResults={setReturnSearchResults}
          setReturnOrderMatches={setReturnOrderMatches}
          setReturnSearched={setReturnSearched}
          setSelectedReturnCustomer={setSelectedReturnCustomer}
          setReturnCustomerOrders={setReturnCustomerOrders}
          setLookedUpOrder={setLookedUpOrder}
          searchInputRef={searchInputRef}
          searchQuery={searchQuery}
          dispatch={dispatch}
          setProductSearch={setProductSearch}
          searchResults={searchResults}
          isSearchingProducts={isSearchingProducts}
          searchHasMore={searchHasMore}
          isFetchingMoreResults={isFetchingMoreResults}
          loadMoreSearchResults={loadMoreSearchResults}
          searchHoverIndex={searchHoverIndex}
          handleAddToCart={handleAddToCart}
          setSearchHoverIndex={setSearchHoverIndex}
          getProductStock={getProductStock}
          onOpenStockLookup={() => openStockLookup()}
          handleBillingModeChange={requestBillingModeChange}
          giftVouchersEnabled={posGiftVouchersEnabled}
          isIncentiveProduct={incentivesAccess ? isIncentiveProduct : undefined}
          onOpenVoucherSales={(q) => setGvSales({ open: true, q })}
          onOpenBalanceCheck={posGiftVouchersEnabled ? () => setGvBalanceOpen(true) : undefined}
          onOpenIncentives={openIncentives}
          giftVoucherPanelProps={{
            types: gvTypes,
            isLoadingTypes: isLoadingGvTypes,
            onPickBuyer: () => setGvBuyerPickerOpen(true),
            onEditPeople: (lineKey) => setGvRecipients({ open: true, lineKey, tab: 'list' }),
          }}
          categories={categories}
          isLoadingCategories={isLoadingCategories}
          selectedTab={selectedTab}
          setSelectedTab={setSelectedTab}
          scrollbarStyle={scrollbarStyle}
          activeCategory={activeCategory}
          productsToShow={productsToShow}
          isLoadingCategoryProducts={isLoadingCategoryProducts}
          categoryHasMore={categoryHasMore}
          isFetchingMoreCategoryProducts={isFetchingMoreCategoryProducts}
          loadMoreCategoryProducts={loadMoreCategoryProducts}
          inStockOnly={inStockOnly}
          setInStockOnly={setInStockOnly}
          setIsCatalogView={setIsCatalogView}
          inExchangeWorkflow={inExchangeWorkflow}
          exchangeSourceOrder={exchangeSourceOrder}
          exchangeReturnItems={exchangeReturnItems}
          setExchangeReturnItems={setExchangeReturnItems}
          reasonCodes={reasonCodes}
          exchangeReturnTotal={exchangeReturnTotal}
          exchangeNewItems={exchangeNewItems}
          setExchangeNewItems={setExchangeNewItems}
          exchangeNearMissHints={exchangeNearMissHints}
          exchangeAppliedPromotions={exchangeAppliedPromotions}
          handleAddExchangeNewItem={handleAddExchangeNewItem}
          handleCancelExchange={handleCancelExchange}
          exchangeCustomer={exchangeCustomer}
          exchangeSalesperson={exchangeSalesperson}
          setExchangeSalesperson={setExchangeSalesperson}
          returnSearchResults={returnSearchResults}
          returnOrderMatches={returnOrderMatches}
          returnSearched={returnSearched}
          selectedReturnCustomer={selectedReturnCustomer}
          isSearchingCustomers={isSearchingCustomers}
          isSelectingOrder={isSelectingOrder}
          handleSelectExchangeOrder={handleSelectExchangeOrder}
          handleSelectReturnOrder={handleSelectReturnOrder}
          handleSelectReturnCustomer={handleSelectReturnCustomer}
          lookedUpOrder={lookedUpOrder}
          returnDrawerOpenDash={returnDrawerOpenDash}
          setReturnDrawerOpenDash={setReturnDrawerOpenDash}
          returnCustomerOrders={returnCustomerOrders}
          isLoadingCustomerOrders={isLoadingCustomerOrders}
          cart={cart}
          selectedProduct={selectedProduct}
          setSelectedProduct={setSelectedProduct}
          orderCartItems={orderCartItems}
          bill_discount_id={bill_discount_id}
          bill_promotion_name={bill_promotion_name}
          updateQty={updateQty}
          adjustQty={adjustQty}
          calculateItemTax={calculateItemTax}
          removeFromCart={removeFromCart}
          applied_promotions={applied_promotions}
          near_miss_hints={near_miss_hints}
          nearMissDismissed={nearMissDismissed}
          setNearMissDismissed={setNearMissDismissed}
          customerDetail={customerDetail}
          selectedCustomer={selectedCustomer}
          isCustomerDetailLoading={isCustomerDetailLoading}
          setIsCustomerDetailModalOpen={setIsCustomerDetailModalOpen}
          setIsCustomerModalOpen={setIsCustomerModalOpen}
          handleRemoveCustomer={handleRemoveCustomer}
          handleRemoveItemSalesperson={handleRemoveItemSalesperson}
          selectedSalesperson={selectedSalesperson}
          setIsSalespersonDrawerOpen={setIsSalespersonDrawerOpen}
          handleRemoveOrderSalesperson={handleRemoveOrderSalesperson}
          isCatalogView={isCatalogView}
          catalogFocusIndex={catalogFocusIndex}
          setCatalogFocusIndex={setCatalogFocusIndex}
          catalogFocusZone={catalogFocusZone}
          setCatalogFocusZone={setCatalogFocusZone}
          bill_discount_amount={bill_discount_amount}
          modeSelectRef={modeSelectRef}
          actionButtonRefs={actionButtonRefs}
          handleResume={handleResume}
        />

        <DashboardRightPanel
          actionButtons={inGiftVoucherMode ? gvActionButtons : actionButtons}
          giftVoucherSlots={gvRightSlots}
          actionButtonRefs={actionButtonRefs}
          isCatalogView={isCatalogView}
          catalogFocusIndex={catalogFocusIndex}
          setCatalogFocusIndex={setCatalogFocusIndex}
          catalogFocusZone={catalogFocusZone}
          setCatalogFocusZone={setCatalogFocusZone}
          selectedProduct={selectedProduct}
          selectedTaxInfo={selectedTaxInfo}
          getProductStock={getProductStock}
          onOpenStockLookup={openStockLookup}
          isMobile={isMobile}
          selectedTab={selectedTab}
          setSelectedTab={setSelectedTab}
          categories={categories}
          isLoadingCategories={isLoadingCategories}
          scrollbarStyle={scrollbarStyle}
          activeCategory={activeCategory}
          productsToShow={productsToShow}
          isLoadingCategoryProducts={isLoadingCategoryProducts}
          categoryHasMore={categoryHasMore}
          isFetchingMoreCategoryProducts={isFetchingMoreCategoryProducts}
          loadMoreCategoryProducts={loadMoreCategoryProducts}
          inStockOnly={inStockOnly}
          setInStockOnly={setInStockOnly}
          handleAddToCart={handleAddToCart}
          cartAddDisabled={cartAddDisabled}
          cartAddDisabledHint={cartAddDisabledHint}
          setIsCatalogView={setIsCatalogView}
          inExchangeWorkflow={inExchangeWorkflow}
          orderNotes={orderNotes}
          setOrderNotes={setOrderNotes}
          handleNotesBlur={handleNotesBlur}
          currentSession={currentSession}
          setIsCashMovementModalOpen={setIsCashMovementModalOpen}
          onOpenCashOffice={openCashOffice}
          cashInOutEnabled={cashInOutEnabled}
          cashOfficeIncoming={cashOfficeIncoming}
          setIsCloseSessionModalOpen={requestCloseSession}
          setIsOpenSessionModalOpen={setIsOpenSessionModalOpen}
          store={store}
          terminal={terminal}
          cart={cart}
          currentOrderId={currentOrderId}
          billExpanded={billExpanded}
          setBillExpanded={setBillExpanded}
          denomBreakdownExpanded={denomBreakdownExpanded}
          setDenomBreakdownExpanded={setDenomBreakdownExpanded}
          exchangeReturnTotal={exchangeReturnTotal}
          exchangeNewTotal={exchangeNewTotal}
          exchangeDirection={exchangeDirection}
          exchangeNetAmount={exchangeNetAmount}
          gross_subtotal={gross_subtotal}
          subtotal={subtotal}
          discount_total={discount_total}
          tax_total={tax_total}
          orderCartItems={orderCartItems}
          bill_discount_id={bill_discount_id}
          bill_discount_name={bill_discount_name}
          bill_discount_amount={bill_discount_amount}
          total_promo_savings={total_promo_savings}
          applied_promotions={applied_promotions}
          bill_promotion_discount_amount={bill_promotion_discount_amount}
          bill_promotion_name={bill_promotion_name}
          displayTotal={displayTotal}
          handleCancelExchange={handleCancelExchange}
          allReturnItemsHaveReasonCode={allReturnItemsHaveReasonCode}
          canCompleteExchange={canCompleteExchange}
          exchangeBlockReason={exchangeBlockReason}
          sessionBlocked={sessionBlocked}
          setExchangeCheckoutOpen={setExchangeCheckoutOpen}
          handleDiscard={handleDiscard}
          customerRequired={customerRequired}
          salespersonRequired={salespersonRequired}
          selectedCustomer={selectedCustomer}
          selectedSalesperson={selectedSalesperson}
          checkoutBlocked={checkoutBlocked}
          setIsCheckoutOpen={setIsCheckoutOpen}
          billingMode={billingMode}
          handleAddExchangeNewItem={handleAddExchangeNewItem}
        />

        <DashboardMobileControls
          billingMode={billingMode}
          inExchangeWorkflow={inExchangeWorkflow}
          actionButtons={inGiftVoucherMode ? gvActionButtons : actionButtons}
          giftVoucherSlots={gvMobileSlots}
          onOpenBalanceCheck={posGiftVouchersEnabled ? () => setGvBalanceOpen(true) : undefined}
          onOpenIncentives={openIncentives}
          currentSession={currentSession}
          setIsCashMovementModalOpen={setIsCashMovementModalOpen}
          onOpenCashOffice={openCashOffice}
          cashInOutEnabled={cashInOutEnabled}
          cashOfficeIncoming={cashOfficeIncoming}
          setIsCloseSessionModalOpen={requestCloseSession}
          setIsOpenSessionModalOpen={setIsOpenSessionModalOpen}
          orderNotes={orderNotes}
          setOrderNotes={setOrderNotes}
          handleNotesBlur={handleNotesBlur}
          exchangeReturnTotal={exchangeReturnTotal}
          exchangeNewTotal={exchangeNewTotal}
          gross_subtotal={gross_subtotal}
          subtotal={subtotal}
          discount_total={discount_total}
          tax_total={tax_total}
          orderCartItems={orderCartItems}
          billExpanded={billExpanded}
          denomBreakdownExpanded={denomBreakdownExpanded}
          setDenomBreakdownExpanded={setDenomBreakdownExpanded}
          bill_discount_id={bill_discount_id}
          bill_discount_name={bill_discount_name}
          bill_discount_amount={bill_discount_amount}
          currentOrderId={currentOrderId}
          exchangeDirection={exchangeDirection}
          exchangeReturnItemsPayload={exchangeReturnItemsPayload}
          exchangeNewItems={exchangeNewItems}
          cart={cart}
          store={store}
          exchangeNetAmount={exchangeNetAmount}
          displayTotal={displayTotal}
          setBillExpanded={setBillExpanded}
          handleCancelExchange={handleCancelExchange}
          allReturnItemsHaveReasonCode={allReturnItemsHaveReasonCode}
          canCompleteExchange={canCompleteExchange}
          exchangeBlockReason={exchangeBlockReason}
          sessionBlocked={sessionBlocked}
          setExchangeCheckoutOpen={setExchangeCheckoutOpen}
          handleDiscard={handleDiscard}
          customerRequired={customerRequired}
          salespersonRequired={salespersonRequired}
          selectedCustomer={selectedCustomer}
          selectedSalesperson={selectedSalesperson}
          checkoutBlocked={checkoutBlocked}
          setIsCheckoutOpen={setIsCheckoutOpen}
          onOpenStockLookup={() => openStockLookup()}
          inStockOnly={inStockOnly}
          setInStockOnly={setInStockOnly}
        />

        {/* ══════════════════════════════════════════
            MOBILE BOTTOM CONTROLS (xs/sm only)
            Action strip + billing summary + buttons
        ══════════════════════════════════════════ */}
      </Box>

      <ReturnItemsDrawer
        open={returnDrawerOpenDash}
        onClose={() => setReturnDrawerOpenDash(false)}
        order={lookedUpOrder}
      />
      <ExchangeCheckoutModal
        open={exchangeCheckoutOpen}
        onClose={() => setExchangeCheckoutOpen(false)}
        onSuccess={() => { setExchangeCheckoutOpen(false); handleBillingModeChange('billing'); }}
        onHandoffToCheckout={async (pendingOrderId) => {
          // customer_pays: the exchange's return side is already processed —
          // resume its new-items draft order like any held bill, then jump
          // straight into checkout to collect the difference. Resume BEFORE
          // switching billing mode: handleResume awaits a couple of network
          // round trips, and flipping to 'billing' first used to swap the
          // exchange panel for the (still-empty, cart not populated yet)
          // normal billing cart underneath — a visible empty-cart flash
          // before checkout opened on top of it. Populating the cart first
          // means there's nothing empty left to flash.
          setExchangeCheckoutOpen(false);
          await handleResume({ id: pendingOrderId });
          handleBillingModeChange('billing');
          setIsCheckoutOpen(true);
        }}
        sourceOrder={exchangeSourceOrder}
        returnItemsPayload={exchangeReturnItemsPayload}
        newItemsPayload={exchangeNewItemsPayload}
        returnTotal={exchangeReturnTotal}
        newTotal={exchangeNewTotal}
        orgSettings={orgSettings}
      />
    </Box>
  );
};

export default Dashboard;
